import { Box, HStack, LinkBox, LinkOverlay } from "@chakra-ui/react"
import { useScrollApp } from "@pathwright/utils/src/scroll/useScroll"
import UserAvatar from "@pathwright/ux/src/components/avatar/UserAvatar"
import BoldThemeHeader from "@pathwright/ux/src/components/header/BoldThemeHeader"
import Link from "@pathwright/ux/src/components/link/Link"
import { motion, useMotionValueEvent, useTransform } from "framer-motion"
import { useRef, useState } from "react"
import { useIntersection } from "react-use"
import { usePathwrightContext } from "../pathwright/PathwrightContext"
import HomeBackground from "./components/ui/HomeBackground"

const HomeHeader = () => {
  const { me } = usePathwrightContext()

  const targetRef = useRef<HTMLDivElement>(null)

  // We only enable the motion animation after the first scroll event.
  // This is due to a visual glitch when first mounting the component
  // due to a scale keframe animation applied to the parent.
  const [enableAnimation, setEnableAnimation] = useState(false)

  let { scrollY } = useScrollApp({
    target: targetRef,
    offset: ["start end", "end start"]
  })

  // Offset scroll animation by the top offset of the container.
  const intersection = useIntersection(targetRef, {
    root: null,
    threshold: 1
  })
  const topOffset = intersection?.boundingClientRect.top || 0

  // Animate size of the header based on scroll position.
  const scale = useTransform(
    scrollY,
    [0 + topOffset, 120 + topOffset],
    [1, 0.8]
  )

  // Only enabling the animation after the first scroll event.
  useMotionValueEvent(scrollY, "change", () => {
    if (!enableAnimation) setEnableAnimation(true)
  })

  // When tapped/clicked: if scrolled down, scroll to top instead of
  // navigating to settings. If already at top, allow normal navigation.
  // Uses capture phase so the event is intercepted before the inner
  // Link component can handle it and trigger navigation.
  const handleHeaderClick = (e: React.MouseEvent) => {
    const scrollContainer = targetRef.current?.closest(
      ".ScrollView"
    ) as HTMLElement | null
    const scrollEl = scrollContainer || document.documentElement
    if (scrollEl.scrollTop > 5) {
      e.preventDefault()
      e.stopPropagation()
      scrollEl.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  return (
    <Box ref={targetRef} className="HomeHeader" w="100%" zIndex={10}>
      <Box
        pos="absolute"
        inset={0}
        // Hack to ensure bg spans the entire paths list so it covers everything in it.
        // This is necessary due to the augmentation layout. We span to the left and right
        // of the paths list to to cover enlarged paths when you hover. Sigh.
        left="-2"
        right="-2"
        css={{
          maskImage:
            "linear-gradient(to top, transparent, rgba(0, 0, 0, .75) 30%, black);"
        }}
      >
        <HomeBackground />
      </Box>
      <Box
        asChild
        w="100%"
        px={4}
        mt={4}
        mb={6}
        // Make the full header region tappable so it's a forgiving
        // touch target on mobile when the header is scaled down.
        pointerEvents="auto"
        cursor="pointer"
        onClickCapture={handleHeaderClick}
      >
        <motion.div style={{ scale: enableAnimation ? scale : 1 }}>
          <LinkBox
            as={HStack}
            w="100%"
            justifyContent="center"
            pos="relative"
            gap={3}
          >
            <LinkOverlay asChild>
              <Link
                relativeTo="?panel=/settings"
                variant="plain"
                textDecor="none"
                gap={2}
              >
                <HStack gap={3} minW={0} maxW="100%" justify="center">
                  <UserAvatar
                    user={me}
                    size={["lg", "xl", "2xl"]}
                    onClick={handleHeaderClick}
                    cursor="pointer"
                    pointerEvents="auto"
                  />
                  <BoldThemeHeader
                    pos="relative"
                    color="whiteAlpha.900"
                    onClick={handleHeaderClick}
                    cursor="pointer"
                    pointerEvents="auto"
                    m={0}
                    // lineHeight={{ base: "1" }}
                  >
                    {me.full_name}
                  </BoldThemeHeader>
                </HStack>
              </Link>
            </LinkOverlay>
          </LinkBox>
        </motion.div>
      </Box>
    </Box>
  )
}

export default HomeHeader
